import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext.tsx";
import Button from "../../components/Button/Button.tsx";
import * as S from "./account.style.ts";
import Link from "../../components/Link/Link.tsx";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import Input from "../../components/Input/Input.tsx";
import ModalComponent from "../../components/Modal/Modal.tsx";
import { budgetAtom } from "../../hook/BudgetAtom.ts";
import { incomeAtom } from "../../hook/IncomeAtom.ts";
import { expenseAtom } from "../../hook/ExpenseAtom.ts";
import Loading from "../../components/Loading/Loading.tsx";
import { deleteUser, updateUserCurrency } from "../../requests/users.ts";
import AccountNav from "../../views/AccountNav/AccountNav.tsx";
import { checkIsExpiredSession, getDateInfo } from "../../functions/helper.ts";
import { userAtom } from "../../hook/UserAtom.ts";
import { ownerPayConfigAtom } from "../../hook/OwnerPayConfigAtom.ts";
import { modeLabels } from "../../functions/cashflowCopy.ts";
import ViewIcon from "../../svg/ViewIcon.tsx";
import RemoveAccountIcon from "../../svg/RemoveAccountIcon.tsx";
import { trackError, trackEvent } from "../../functions/mixpanel.ts";
import SelectComponent from "../../components/Select/Select.tsx";
import { currencyList } from "../../constants.ts";
import SessionExpired from "../../components/SessionExpired/SessionExpired.tsx";
import AddIcon from "../../svg/AddIcon.tsx";
import ArrowIcon from "../../svg/ArrowIcon.tsx";
import CurrencyIcon from "../../svg/CurrencyIcon.tsx";
import CancelIcon from "../../svg/CancelIcon.tsx";

const Account = () => {
  const { logout, getAccessTokenSilently } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const [isLoading, setIsloading] = useState<boolean>(false);
  const setBudget = useSetAtom(budgetAtom);
  const setIncome = useSetAtom(incomeAtom);
  const setExpense = useSetAtom(expenseAtom);
  const [currentUser, setCurrentUser] = useAtom(userAtom);
  const cashflowLabels = modeLabels(useAtomValue(ownerPayConfigAtom));
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>(
    params.get("nav") || (currentUser && !currentUser.hasBudget)
      ? "settings"
      : "cash-flow",
  );
  const [currencyModal, setCurrencyModal] = useState<boolean>(false);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const defaultCurrency = currencyList.filter(
    (item) => item.label === currentUser?.currency,
  )[0];
  const [userCurrency, setUserCurrency] = useState<string>(
    defaultCurrency?.label,
  );
  const { currentMonth, currentYear } = getDateInfo();

  const logOutAccount = () => {
    setIsloading(true);
    setBudget([]);
    setIncome([]);
    setExpense([]);
    logout();
  };

  const deleteAccount = async () => {
    setDeleteError(false);

    try {
      const accessToken = await getAccessTokenSilently();

      const result = await deleteUser(accessToken);

      if (result.success) {
        trackEvent("Delete Account");
        logOutAccount();
      } else {
        setDeleteError(true);
      }
    } catch (err) {
      trackError("Account - deleteAccount:", { result: err });

      if (checkIsExpiredSession(err)) {
        setIsSessionExpired(true);
      }
    }
  };

  const updateCurrency = async () => {
    try {
      const accessToken = await getAccessTokenSilently();

      currentUser &&
        setCurrentUser({
          ...currentUser,
          currency: userCurrency,
        });

      await updateUserCurrency(accessToken, { currency: userCurrency });
      trackEvent("Update User Currency");
    } catch (err) {
      trackError("Account - updateCurrency:", { result: err });

      if (checkIsExpiredSession(err)) {
        setIsSessionExpired(true);
      }
    }
  };

  if (isLoading) {
    return <Loading isText />;
  }

  return (
    <>
      <S.Wrapper>
        <AccountNav
          setSelectedOption={setSelectedOption}
          selectedOption={selectedOption}
        />
        <S.ContentWrapper>
          <>
            {selectedOption === "settings" && (
              <>
                <S.Section>
                  <Input
                    label="email"
                    labelValue="Email:"
                    onChange={() => {}}
                    placeHolder="Enter your email"
                    isDisabled
                    defaultValue={currentUser?.email || ""}
                    inputType="text"
                  />
                </S.Section>
                <S.Section>
                  <Button
                    handleClick={() => setIsOpen(true)}
                    buttonSize="medium"
                    classType="text"
                  >
                    <div className="linkWrapper">
                      <span>
                        <RemoveAccountIcon />
                        <span className="textContainer">
                          <span>Delete Account</span>
                          <span className="subText">
                            Permanently delete your account
                          </span>
                        </span>
                      </span>
                      <ArrowIcon />
                    </div>
                  </Button>
                </S.Section>
                <S.Section>
                  <Button classType="text" handleClick={logOutAccount}>
                    <div className="linkWrapper">
                      <span>
                        <CancelIcon />
                        <span className="textContainer">
                          <span>Logout</span>
                          <span className="subText">
                            Log out securely from your account
                          </span>
                        </span>
                      </span>
                      <ArrowIcon />
                    </div>
                  </Button>
                </S.Section>
              </>
            )}
            {selectedOption === "cash-flow" && (
              <>
                <S.Section>
                  <Link url="/income" label="Income">
                    <div className="linkWrapper">
                      <span>
                        <AddIcon />
                        <span className="textContainer">
                          <span>Income</span>
                          <span className="subText">
                            Log payments and set your recurring pay
                          </span>
                        </span>
                      </span>
                      <ArrowIcon />
                    </div>
                  </Link>
                </S.Section>
                <S.Section>
                  <Link
                    url="/owner-pay"
                    label={cashflowLabels.accountLinkLabel}
                  >
                    <div className="linkWrapper">
                      <span>
                        <CurrencyIcon />
                        <span className="textContainer">
                          <span>{cashflowLabels.accountLinkLabel}</span>
                          <span className="subText">
                            {cashflowLabels.accountLinkSub}
                          </span>
                        </span>
                      </span>
                      <ArrowIcon />
                    </div>
                  </Link>
                </S.Section>
                <S.Section>
                  <Link
                    url={`/monthly/expense/${currentMonth}/${currentYear}`}
                    label={`${currentMonth} expenses`}
                  >
                    <div className="linkWrapper">
                      <span>
                        <ViewIcon />
                        <span className="textContainer">
                          <span>{currentMonth} expenses</span>
                          <span className="subText">
                            Edit this month&apos;s recurring expenses
                          </span>
                        </span>
                      </span>
                      <ArrowIcon />
                    </div>
                  </Link>
                </S.Section>
                <S.Section>
                  <Button
                    handleClick={() => setCurrencyModal(true)}
                    buttonSize="medium"
                    classType="text"
                  >
                    <div className="linkWrapper">
                      <span>
                        <CurrencyIcon />
                        <span className="textContainer">
                          <span>Change Currency</span>
                          <span className="subText">
                            Select your preferred currency for your budget
                            tracking
                          </span>
                        </span>
                      </span>
                      <ArrowIcon />
                    </div>
                  </Button>
                </S.Section>
              </>
            )}
            <ModalComponent
              isOpen={isOpen}
              onClose={() => {
                setIsOpen(false);
                setDeleteError(false);
              }}
              title={`Confirm Account Deletion`}
            >
              <S.ModalWrapper>
                <span>Are you sure you want to delete your account?</span>
                <S.ModalBtn>
                  <Button
                    buttonSize="small"
                    handleClick={deleteAccount}
                    classType="register"
                  >
                    Yes
                  </Button>
                  <Button
                    buttonSize="small"
                    handleClick={() => {
                      setIsOpen(false);
                      setDeleteError(false);
                    }}
                  >
                    No
                  </Button>
                </S.ModalBtn>
                {deleteError && (
                  <S.ErrorMsg>
                    Your account cannot be deleted at this time.
                    <br />
                    Please try again or contact support.
                  </S.ErrorMsg>
                )}
              </S.ModalWrapper>
            </ModalComponent>
            <ModalComponent
              isOpen={currencyModal}
              onClose={() => setCurrencyModal(false)}
              title={`Change Your Currency`}
            >
              <S.ModalWrapper>
                <SelectComponent
                  options={currencyList}
                  placeHolder="Currency"
                  defaultValue={defaultCurrency?.label}
                  setOption={(val) => setUserCurrency(val)}
                />
                <S.ModalBtn>
                  <Button
                    buttonSize="small"
                    handleClick={() => setCurrencyModal(false)}
                    classType="register"
                  >
                    Cancel
                  </Button>
                  <Button buttonSize="small" handleClick={updateCurrency}>
                    Submit
                  </Button>
                </S.ModalBtn>
              </S.ModalWrapper>
            </ModalComponent>
          </>
        </S.ContentWrapper>
        <SessionExpired
          isOpen={isSessionExpired}
          closeModal={setIsSessionExpired}
        />
      </S.Wrapper>
    </>
  );
};

export default Account;
